import { useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, Upload, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { media } from "@/pages/PublicHome";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const getIn = (o, path) => path.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
export const setIn = (o, path, v) => {
  const [k, ...rest] = path.split(".");
  return { ...o, [k]: rest.length ? setIn(o?.[k] || {}, rest.join("."), v) : v };
};

const MediaField = ({ value, onChange, accept, testid }) => {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { onChange((await api.post("/admin/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.id); toast.success("Yüklendi"); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      {value && (accept.startsWith("video") ? <video src={media(value)} className="h-12 rounded" /> : <img src={media(value)} alt="" className="h-12 rounded border bg-white" />)}
      <label className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 cursor-pointer"><Upload className="w-3.5 h-3.5" />{busy ? "Yükleniyor..." : value ? "Değiştir" : "Yükle"}
        <input data-testid={testid} type="file" accept={accept} className="hidden" onChange={up} /></label>
      {value && <button type="button" onClick={() => onChange(null)} className="text-red-600"><X className="w-3.5 h-3.5" /></button>}
    </div>
  );
};

export const Field = ({ f, value, onChange, testid }) => {
  const t = f.type || "text";
  return (
    <div className={t === "textarea" ? "md:col-span-2" : ""}>
      <Label className="text-xs">{f.label}</Label>
      {t === "textarea" ? <Textarea data-testid={testid} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        : t === "switch" ? <div className="pt-1"><Switch data-testid={testid} checked={!!value} onCheckedChange={onChange} /></div>
        : t === "image" ? <MediaField testid={testid} value={value} onChange={onChange} accept="image/png,image/jpeg,image/webp,image/gif,image/x-icon" />
        : t === "video" ? <MediaField testid={testid} value={value} onChange={onChange} accept="video/mp4,video/webm" />
        : t === "color" ? <div className="flex gap-2"><input type="color" data-testid={testid} value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border" /><Input value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>
        : t === "select" ? <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm">{f.options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        : <Input data-testid={testid} type={t} value={value ?? ""} onChange={(e) => onChange(t === "number" ? Number(e.target.value) : e.target.value)} />}
    </div>
  );
};

export const Fields = ({ fields, obj, onChange, prefix }) => (
  <div className="grid md:grid-cols-2 gap-3">
    {fields.map((f) => <Field key={f.key} f={f} testid={`cms-${prefix}-${f.key.replace(/\./g, "-")}`} value={getIn(obj, f.key)} onChange={(v) => onChange(setIn(obj, f.key, v))} />)}
  </div>
);

export const ListEditor = ({ items = [], fields, onChange, prefix, addLabel = "Ekle", blank = {} }) => {
  const move = (i, d) => { const n = [...items]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div className="space-y-2" data-testid={`cms-list-${prefix}`}>
      {items.map((it, i) => (
        <div key={it.id} className="rounded-lg border p-3 bg-white space-y-2" data-testid={`cms-${prefix}-item-${i}`}>
          <div className="flex items-center gap-1 justify-end">
            <label className="flex items-center gap-1.5 text-xs mr-auto"><Switch data-testid={`cms-${prefix}-visible-${i}`} checked={it.visible !== false} onCheckedChange={(v) => onChange(items.map((x, j) => (j === i ? { ...x, visible: v } : x)))} />Yayında</label>
            <Button type="button" size="icon" variant="ghost" className="h-7 w-7" data-testid={`cms-${prefix}-up-${i}`} onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
            <Button type="button" size="icon" variant="ghost" className="h-7 w-7" data-testid={`cms-${prefix}-down-${i}`} onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
            <Button type="button" size="icon" variant="ghost" className="h-7 w-7" data-testid={`cms-${prefix}-delete-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
          </div>
          <Fields fields={fields} obj={it} prefix={`${prefix}-${i}`} onChange={(v) => onChange(items.map((x, j) => (j === i ? v : x)))} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" data-testid={`cms-${prefix}-add`} onClick={() => onChange([...items, { id: Math.random().toString(36).slice(2, 10), visible: true, ...blank }])}><Plus className="w-4 h-4 mr-1" />{addLabel}</Button>
    </div>
  );
};
